// ── THE PORTAL LAW ──────────────────────────────────────────────────────────
// A page and its picture are the same place.
//
// For a year they were two. A page took its colour from the ROOM it belongs to
// (lib/company/theme: blog is red on lavender, updates is blue, a comparison
// wears the other product's own hue); its thumbnail took its colour from a HASH
// of the slug, which landed on one of sixteen curated palettes and then chose
// the creature standing FURTHEST from that ground. Both systems were carefully
// built and neither knew the other existed, so a deep green card opened a pale
// lavender page with a red netti in it. Every card was a door painted a
// different colour from the room behind it.
//
// This is the one door now. Ask it about a surface and it hands back both
// faces, built from the same two numbers:
//
//   theme  what the PAGE wears — the pale ground, the live organism, its age.
//   card   what the PICTURE wears — THE SAME HUE, deepened, and THE SAME
//          creature, baked. Only the value differs, because a card is read at
//          thumbnail size in a feed and the page's pale ground would dissolve
//          there (see CARD_DEPTH).
//
// Two things a new surface gets for free by asking here:
//
//   A ROOM IS STILL A ROOM. The identity hue is the room's (CO_ROOMS), so the
//   blog stream still reads as one stream from the corner of your eye.
//
//   A PAGE IS STILL ITSELF. Within its room, a post's hue is shaded by its own
//   seed (SHADE_SPREAD) — so no two letters are twins, and the page you land on
//   wears that exact shade, not the room's average.
//
// Anything under /company must appear in COMPANY_SURFACES below; portal.test.ts
// walks the app directory and fails on a page that forgot to.

import {
  ABOUT_THEME,
  CO_ROOMS,
  CO_THEMES,
  GROUND_SAT,
  GROUND_LEAD,
  TEAM_THEME,
  compareTheme,
  groundFor,
  organismFor,
  roomForKind,
  type CoRoom,
  type CoTheme,
  type Tone,
} from '../company/theme';
import { seedFromString } from '../organism/identity';
import { representativeOrganismHue } from '../organism/color';
import { portalPalette, type ThumbPalette } from './thumbPalette';
import { BRAND } from './marks';
import { ART_SUFFIX, POST_ART, artForSeed } from "./postArtRegistry";

/**
 * How far a single page may drift from its room's identity hue, in degrees
 * either way.
 *
 * Wide enough that two letters never look like the same letter; narrow enough
 * that every letter still looks like the blog. 22° was measured against the
 * house interval itself (GROUND_LEAD, 59°) — a page may move about a third of
 * the way toward its own ground's hue and no further, so a shade can never
 * cross into the neighbouring room.
 */
export const SHADE_SPREAD = 22;

/** a seeded 0..1 stream (mulberry32) — the same generator the palettes use */
function rngFrom(seed: number): () => number {
  let a = seed || 1;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const wrap = (h: number) => ((h % 360) + 360) % 360;

/** a page's own hue: its room's, moved by its own seed and never further. */
export function shadeHue(roomHue: number, seed: string): number {
  return wrap(roomHue + (rngFrom(seedFromString(seed))() * 2 - 1) * SHADE_SPREAD);
}

export type PortalRoom = CoRoom | 'about' | 'team' | 'house';

export interface Portal {
  /** the room a reader should recognise without reading anything */
  room: PortalRoom;
  /** this place's identity hue — the organism's own, on the page and in the picture */
  hue: number;
  /** what the page wears */
  theme: CoTheme;
  /** what the picture wears */
  card: ThumbPalette;
}

/** Both faces of one place. */
function portal(room: PortalRoom, theme: CoTheme, seed: string): Portal {
  return {
    room,
    hue: theme.color.primary.hue,
    theme,
    card: portalPalette({
      pageGround: theme.ground,
      state: theme.color,
      // WHAT THE CREATURE READS AS, not what its primary says. A netti is three
      // hues composited — the page's bloom is the shader's blend of them, and
      // the card's is one image turned to a single hue, so the two only agree
      // if the card aims at the blend. `representativeOrganismHue` is that
      // number, and it is the same one the UI chrome already matches against.
      nettiHue: representativeOrganismHue(theme.color),
      seed,
    }),
  };
}

// ── the surfaces ────────────────────────────────────────────────────────────

/** A stream's own index — /company, /company?tab=updates, /company/compare. */
export function portalForRoom(room: CoRoom): Portal {
  return portal(room, CO_THEMES[room], `room:${room}`);
}

/**
 * One post — a letter, a release note, a dispatch.
 *
 * `hue` is the composer's nudge: it does not name a colour, it re-seeds the
 * shade, so a post can be moved off a neighbour it came out too close to
 * without changing the address it lives at.
 */
export function portalForPost(post: {
  slug: string;
  kind: string;
  category?: string | null;
  hue?: number | null;
}): Portal {
  return portalForShade(roomForKind(post.kind, post.category), postSeed(post));
}

/**
 * The seed a post's shade is drawn from. The card generator is handed this
 * rather than the post — it never has the row, only a URL — so the two sides
 * must agree on the seed and nothing else.
 */
export function postSeed(post: { slug: string; hue?: number | null }): string {
  if (POST_ART[post.slug]) return `${post.slug}${ART_SUFFIX}`;
  return post.hue != null ? `${post.slug}#${post.hue}` : post.slug;
}

/** A room, shaded by one seed: the page a post is read on and the card it wears. */
export function portalForShade(room: CoRoom, seed: string): Portal {
  const r = CO_ROOMS[room];
  // a post with art of its own (lib/brand/postArtRegistry) wears its exact hue
  const art = artForSeed(seed);
  const hue = art?.hue ?? shadeHue(r.hue, seed);
  // groundFor(h) paints the ground GROUND_LEAD below the hue it is given, so an
  // explicit ground hue is handed in with the lead added back.
  const ground = art?.groundHue != null ? groundFor(art.groundHue + GROUND_LEAD, GROUND_SAT) : groundFor(hue, GROUND_SAT);
  return portal(room, { ground, color: organismFor(hue, r.sat), age: r.age }, seed);
}

/**
 * One comparison. A comparison already has a hue of its own — the OTHER
 * product's, measured off their icon — so it takes no shade: the page is
 * already the only page in that colour.
 */
export function portalForComparison(comparison: { slug: string; tone: Tone }): Portal {
  return portal('compare', compareTheme(comparison.tone), comparison.slug);
}

/** the two night rooms — the pages about us, where netti IS the room */
export const PORTAL_ABOUT: Portal = portal('about', ABOUT_THEME, 'company:about');
export const PORTAL_TEAM: Portal = portal('team', TEAM_THEME, 'company:team');

/**
 * The house — docs, skills, a shared page. Not a room: these surfaces are the
 * product speaking in its own voice, on the brand ground, under the default
 * bloom. They are in the system so that moving one into a room is one line.
 */
export const PORTAL_HOUSE: Portal = {
  room: 'house',
  hue: representativeOrganismHue(CO_THEMES.compare.color),
  theme: { ground: BRAND.ground, color: CO_THEMES.compare.color, age: 0.8 },
  card: {
    ...portalPalette({
      pageGround: BRAND.ground,
      state: CO_THEMES.compare.color,
      nettiHue: CO_THEMES.compare.color.primary.hue,
      seed: 'house',
    }),
    // the house card is the one card that is NOT a portal into a coloured room:
    // it is the brand ground itself, with the brand's own default bloom.
    ground: BRAND.ground,
    groundFrom: BRAND.ground,
    groundTo: BRAND.ground,
    ink: BRAND.ink,
    accent: BRAND.blue,
    nettiSrc: '/brand/netti-default.png',
  },
};

// ── the registry ────────────────────────────────────────────────────────────

/**
 * Every surface under /company and which portal opens it. A page that is not
 * listed here has no colour agreement between itself and its picture, so the
 * test fails until it is added — that is the whole mechanism by which a NEW
 * page aligns without anyone remembering to align it.
 */
export const COMPANY_SURFACES = {
  '/company': 'room:blog',
  '/company/[slug]': 'post',
  '/company/about': 'about',
  '/company/team': 'team',
  '/company/compare': 'room:compare',
  '/company/compare/[slug]': 'comparison',
  /** the founder-only composer — it writes in the blog's room */
  '/company/compose': 'room:blog',
} as const satisfies Record<string, string>;

export type CompanySurface = keyof typeof COMPANY_SURFACES;

/** The portal for a STATIC company route (the dynamic ones need their row). */
export function portalForRoute(route: string): Portal | null {
  const source = (COMPANY_SURFACES as Record<string, string>)[route];
  if (!source) return null;
  if (source === 'about') return PORTAL_ABOUT;
  if (source === 'team') return PORTAL_TEAM;
  if (source.startsWith('room:')) return portalForRoom(source.slice(5) as CoRoom);
  return null;
}
